import { MenuItem, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useHistory } from 'react-router';
import { Template, SMS } from 'domains/content/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import CreateNewDropDownItem from 'domains/core/components/CreateNewDropDownItem';
import useTemplates from 'hooks/queries/useTemplates';
import useSmses from 'hooks/queries/useSmses';
import { URLPaths } from 'models/enums';
import ButtonContentPreview from './ButtonContentPreview';
import { AutocompleteOption } from '../Autocomplete/Autocomplete';
import { CampaignChannels } from 'domains/campaigns/types';

// ContentItem can be either a Template or SMS but the component will only handle one type at a time
export type ContentItem = Template | SMS;

export type Props = {
    contentType?: CampaignChannels;
    isPreviewVisible: boolean;
    textfieldInput: string;
    value: AutocompleteOption<ContentItem>;
    onChange: (e: any, option: AutocompleteOption<ContentItem>) => void;
    onContentPreviewClick: () => void;
    onInputChange: (e: any, inputValue: string) => void;
};

const AutocompleteContent = ({
    contentType = CampaignChannels.EMAIL,
    isPreviewVisible,
    textfieldInput,
    value,
    onChange,
    onContentPreviewClick,
    onInputChange,
}: Props) => {
    const history = useHistory();

    const isChannelEmail = contentType === CampaignChannels.EMAIL;
    const isChannelSms = contentType === CampaignChannels.SMS;

    // Only fetch the data we need based on contentType
    const { isSuccess: isTemplatesSuccess, data: templates } = useTemplates({
        enabled: isChannelEmail,
    });
    const { isSuccess: isSmsesSuccess, data: smses } = useSmses({
        enabled: isChannelSms,
    });

    // Create the "Create New" option based on content type
    const createNewOption = useMemo(() => {
        if (isChannelEmail) {
            return {
                index: 0,
                value: { contentType: CampaignChannels.EMAIL } as any,
                label: 'Create new email',
                type: 'email',
            };
        } else {
            return {
                index: 0,
                value: { contentType: CampaignChannels.SMS } as any,
                label: 'Create new SMS',
                type: 'sms',
            };
        }
    }, [isChannelEmail]);

    // Create content options based on type
    const contentOptions = useMemo(() => {
        if (isChannelEmail && isTemplatesSuccess) {
            return templates
                .filter((template) => !template.isLayout)
                .map((template: Template, index: number) => ({
                    index: index + 1,
                    value: template,
                    label: template.name,
                    type: 'email',
                }));
        } else if (isChannelSms && isSmsesSuccess) {
            return smses.map((sms: SMS, index: number) => ({
                index: index + 1,
                value: sms,
                label: sms.name,
                type: 'sms',
            }));
        }
        return [];
    }, [isChannelEmail, isTemplatesSuccess, isChannelSms, isSmsesSuccess, templates, smses]);

    // Combine create option with content options
    const options = useMemo(() => [createNewOption, ...contentOptions], [createNewOption, contentOptions]);

    const handleCreateNew = () => {
        if (isChannelEmail) {
            history.push(`${URLPaths.CONTENT_CREATE_EMAIL}?id=default`);
        } else {
            history.push(`${URLPaths.CONTENT_CREATE_SMS}?id=default`);
        }
    };

    return (
        <Autocomplete
            data-test="content-autocomplete"
            data-testid="content-autocomplete"
            freeSolo={false}
            fullWidth
            getOptionLabel={(option) => option.label ?? ''}
            inputValue={textfieldInput ?? ''}
            isOptionEqualToValue={(option, value) => {
                if (!option.value || !value.value) return false;
                return option.value.id === value.value.id;
            }}
            label="Content"
            leadingIcon={
                value ? (
                    <ButtonContentPreview
                        contentType={contentType}
                        onClick={onContentPreviewClick}
                        isActive={isPreviewVisible}
                    />
                ) : null
            }
            onChange={onChange}
            onInputChange={onInputChange}
            options={options}
            placeholder="Select or create content"
            renderOption={({ className, ...rest }, option) => {
                const isCreateOption = option.index === 0;

                if (isCreateOption) {
                    return (
                        <CreateNewDropDownItem
                            {...rest}
                            key={option.index}
                            value={option.value}
                            onClick={handleCreateNew}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>{option.label}</Typography>
                            </Stack>
                        </CreateNewDropDownItem>
                    );
                }

                return (
                    <MenuItem {...rest} key={`${contentType}-${option.value.id}`} value={option.value}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography>{option.label}</Typography>
                        </Stack>
                    </MenuItem>
                );
            }}
            type="string"
            value={value ?? null}
        />
    );
};

export default AutocompleteContent;
